/**
 * Prize Draw Service (Web / Request App)
 *
 * Mirror of the PWA + Electron services. Reads the DJ's active prize and
 * decides whether a guest's request matches it.
 *
 * Doc path: users/{djUid}/prizeDraw/current
 *
 * Match strategy:
 *   1. trackId equality (preferred — both sides come from a playlist).
 *   2. Normalised "title|artist" fallback for fuzzy matching.
 */

import { doc, getDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../firebase-config.js';

export const normaliseTrackKey = (title, artist) => {
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${norm(title)}|${norm(artist)}`;
};

export const getCurrentPrize = async (djUid) => {
  if (!djUid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', djUid, 'prizeDraw', 'current'));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.warn('[prizeDrawService] Failed to read prize doc:', err);
    return null;
  }
};

/**
 * Single-prize legacy API. Returns boolean.
 * Prefer findWinningPrize() in new code — it supports multiple prizes and
 * returns the specific matched prize so the UI can show ITS description.
 *
 * @param {object} request - either a djchats doc shape or a flat track object
 *                            (title, artist, id)
 * @param {object|null} prize
 */
export const isWinningRequest = (request, prize) => {
  if (!prize || prize.status !== 'active') return false;
  if (!request) return false;

  const inner = request.request || request;
  const reqId = inner.id || inner.trackId;
  if (prize.trackId && reqId && prize.trackId === reqId) return true;

  const reqTitle = inner.track || inner.title;
  const reqArtist = inner.artist;
  const reqKey = normaliseTrackKey(reqTitle, reqArtist);
  if (!reqKey || reqKey === '|') return false;
  return reqKey === prize.trackTitleNormalised;
};

/**
 * Multi-prize matcher. Returns the matched prize entry (with its own
 * trackTitle / prizeDescription / etc.) or null if no match.
 *
 * Accepts the FULL prize doc — it auto-detects whether the doc has a
 * multi-prize `prizes` array or only the legacy single-prize fields.
 */
export const findWinningPrize = (request, prizeDoc) => {
  if (!prizeDoc || !request) return null;

  // Build the list of prizes to check.
  let prizes;
  if (Array.isArray(prizeDoc.prizes) && prizeDoc.prizes.length > 0) {
    // A prize that has been claimed (won + Google-linked) drops out of the
    // active pool so it can't be won again.
    prizes = prizeDoc.prizes.filter(
      (p) => p && (p.status || 'active') === 'active' && !p.claimedBy
    );
  } else if (
    prizeDoc.trackTitle &&
    prizeDoc.status !== 'cleared' &&
    prizeDoc.status !== 'claimed' &&
    !prizeDoc.claimedBy
  ) {
    prizes = [{
      id: 'legacy',
      trackId: prizeDoc.trackId,
      trackTitle: prizeDoc.trackTitle,
      trackArtist: prizeDoc.trackArtist,
      trackCover: prizeDoc.trackCover,
      trackTitleNormalised: prizeDoc.trackTitleNormalised,
      prizeDescription: prizeDoc.prizeDescription || '',
    }];
  } else {
    return null;
  }

  const inner = request.request || request;
  const reqId = inner.id || inner.trackId;
  const reqTitle = inner.track || inner.title;
  const reqArtist = inner.artist;
  const reqKey = normaliseTrackKey(reqTitle, reqArtist);

  for (const p of prizes) {
    if (p.trackId && reqId && p.trackId === reqId) return p;
    if (reqKey && reqKey !== '|' && reqKey === p.trackTitleNormalised) return p;
  }
  return null;
};

/**
 * Atomically claim a prize for a winner — FIRST COME, FIRST SERVED.
 *
 * Multiple guests can request the same winning track before anyone claims it,
 * so claiming runs in a transaction: the first caller to succeed marks the
 * prize `claimed`; every later caller gets `already_won`. This is what stops
 * two people both walking away with the same prize.
 *
 * A prize that is only ever *declined* (forfeited) is never marked here, so it
 * stays in the active pool and the next matching requester can still win it.
 *
 * @returns {Promise<{result:'claimed'|'already_won'|'no_prize'|'error', claimedByName?:string, error?:any}>}
 */
export const claimPrize = async (djUid, prizeId, claimerUid, claimerName) => {
  if (!djUid || !claimerUid) return { result: 'error', error: 'missing-args' };
  const prizeRef = doc(db, 'users', djUid, 'prizeDraw', 'current');
  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(prizeRef);
      if (!snap.exists()) return { result: 'no_prize' };
      const data = snap.data();

      // Multi-prize doc (prizes[] array)
      if (Array.isArray(data.prizes) && data.prizes.length > 0) {
        const prizes = data.prizes.map((p) => ({ ...p }));
        const idx = prizes.findIndex(
          (p) => (p.id || 'legacy') === (prizeId || 'legacy')
        );
        if (idx === -1) return { result: 'no_prize' };
        const p = prizes[idx];
        if ((p.status || 'active') !== 'active' || p.claimedBy) {
          return {
            result: p.claimedBy === claimerUid ? 'claimed' : 'already_won',
            claimedByName: p.claimedByName || '',
          };
        }
        prizes[idx] = {
          ...p,
          status: 'claimed',
          claimedBy: claimerUid,
          claimedByName: claimerName || '',
          // serverTimestamp() sentinels aren't allowed inside array elements,
          // so stamp claim time with the client clock here.
          claimedAt: Timestamp.now(),
        };
        tx.update(prizeRef, { prizes });
        return { result: 'claimed' };
      }

      // Legacy single-prize doc
      if (data.trackTitle && data.status !== 'cleared') {
        if (data.status === 'claimed' || data.claimedBy) {
          return {
            result: data.claimedBy === claimerUid ? 'claimed' : 'already_won',
            claimedByName: data.claimedByName || '',
          };
        }
        tx.update(prizeRef, {
          status: 'claimed',
          claimedBy: claimerUid,
          claimedByName: claimerName || '',
          claimedAt: Timestamp.now(),
        });
        return { result: 'claimed' };
      }

      return { result: 'no_prize' };
    });
  } catch (err) {
    console.warn('[prizeDrawService] claimPrize failed:', err);
    return { result: 'error', error: err };
  }
};
